import { memo, useCallback, useMemo } from "react";
import type { EChartsOption } from "echarts";
import EChart from "./EChart";
import { defaultAxisLabel, defaultGrid, tooltipCss } from "./chartDefaults";
import type { BoxplotData } from "../../config/data";
import type { AxisFormatter } from "./XmrChart";

interface BoxplotChartProps {
  data: BoxplotData;
  axisFormatter?: AxisFormatter;
  axisName?: string;
  selectedDate?: string;
  onDateSelect?: (date: string | null) => void;
}

function BoxplotChart({ data, axisFormatter, axisName, selectedDate, onDateSelect }: BoxplotChartProps) {
  const option = useMemo<EChartsOption>(
    () => buildOption(data, axisFormatter, axisName, selectedDate),
    [data, axisFormatter, axisName, selectedDate],
  );

  const handleClick = useCallback(
    (params: unknown) => {
      if (!onDateSelect) return;
      const p = params as { dataIndex?: number; componentType?: string };
      if (p.componentType !== "series" || p.dataIndex === undefined) return;

      const clickedDate = data.timestamps[p.dataIndex]?.toISOString().split("T")[0];
      if (!clickedDate) return;

      // Toggle: if clicking same date, deselect; otherwise select new date
      onDateSelect(clickedDate === selectedDate ? null : clickedDate);
    },
    [data.timestamps, selectedDate, onDateSelect],
  );

  const onEvents = useMemo(() => (onDateSelect ? { click: handleClick } : undefined), [onDateSelect, handleClick]);

  return <EChart option={option} height={400} onEvents={onEvents} />;
}

function buildOption(
  data: BoxplotData,
  axisFormatter?: AxisFormatter,
  axisName?: string,
  selectedDate?: string,
): EChartsOption {
  // Convert timestamps to category labels for x-axis
  const categories = data.timestamps.map(ts => ts.toISOString().split("T")[0]);
  const selectedIndex = selectedDate ? categories.indexOf(selectedDate) : -1;

  const useLog = data.logScale ?? false;

  // For log scale, transform values; ECharts boxplot expects [min, Q1, median, Q3, max]
  // Clip values to minimum of 1 before taking log (so log(x) >= 0)
  const boxplotData = useLog
    ? data.boxplotValues.map(
        values => values.map(v => Math.log(Math.max(v, 1))) as [number, number, number, number, number],
      )
    : data.boxplotValues;

  const format = axisFormatter ?? ((v: number) => v.toFixed(2));

  // For log scale, format the original (non-log) values in tooltip
  // Handle the case where original value was ≤1 (maps to log ≤0) - show as original value
  const tooltipFormat = useLog
    ? (logVal: number, originalVal: number) => (originalVal <= 1 ? format(originalVal) : format(Math.exp(logVal)))
    : (v: number) => format(v);

  // For y-axis labels in log scale, show the original values
  // Special case: log(1) = 0, but we want to show "0" for values that were originally 0
  const yAxisFormatter = useLog
    ? (v: number) => {
        const originalVal = Math.exp(v);
        // If very close to 1 (meaning original was 0 or 1), show 0
        if (originalVal < 1.01) return format(0);
        return format(originalVal);
      }
    : axisFormatter;

  return {
    animation: false,
    tooltip: {
      trigger: "item",
      extraCssText: tooltipCss,
      formatter: (params: unknown) => {
        const p = params as { dataIndex?: number; componentType?: string };
        if (p.componentType !== "series" || p.dataIndex === undefined) return "";
        const idx = p.dataIndex;
        const logValues = boxplotData[idx];
        const originalValues = data.boxplotValues[idx];
        if (!logValues || !originalValues) return "";

        const fmt = (i: number) =>
          useLog ? tooltipFormat(logValues[i], originalValues[i]) : tooltipFormat(logValues[i], logValues[i]);

        return `
          <strong>${categories[idx]}</strong><br/>
          ${data.extremeLabels.upper}: ${fmt(4)}<br/>
          Q3 (75%): ${fmt(3)}<br/>
          Median (50%): ${fmt(2)}<br/>
          Q1 (25%): ${fmt(1)}<br/>
          ${data.extremeLabels.lower}: ${fmt(0)}
        `;
      },
    },
    xAxis: {
      type: "category",
      data: categories,
      boundaryGap: true,
      axisLabel: {
        rotate: 45,
        fontSize: 10,
      },
    },
    yAxis: {
      type: "value",
      axisLabel: { formatter: yAxisFormatter ?? axisFormatter ?? defaultAxisLabel, margin: 12 },
      name: axisName,
      nameLocation: "end",
      nameGap: 30,
    },
    grid: { ...defaultGrid, bottom: 60 },
    series: [
      {
        name: "Distribution",
        type: "boxplot",
        data: boxplotData.map((values, idx) => ({
          value: values,
          itemStyle:
            selectedIndex === idx
              ? { color: "#F0FF71", borderColor: "#1D1D1F", borderWidth: 2.5 }
              : { color: "#E1E4EE", borderColor: "#1D1D1F", borderWidth: 1.5 },
        })),
        emphasis: {
          itemStyle: {
            borderColor: "#1D1D1F",
            borderWidth: 2,
          },
        },
      },
    ],
  } satisfies EChartsOption;
}

export default memo(BoxplotChart);
